import type { ComponentPropsWithoutRef, PropsWithChildren } from 'react'
import React, { forwardRef } from 'react'
import { split } from '@mote/helpers'
import { __DEV__ } from '@mote/helpers/constants'
import { useMergeRefs } from '@mote/helpers/hooks'
import type { AudioDataMinimal } from '@mote/helpers/media'
import type { AvatarWithTooltipProps } from '../AvatarV2'
import { AvatarWithTooltip } from '../AvatarV2'
import type { MoteCardControlsProps } from '../MoteCardControls'
import { MoteCardControls } from '../MoteCardControls'
import type { MoteDownloadButtonProps } from '../MoteDownloadButton'
import { MoteDownloadButton } from '../MoteDownloadButton'
import { MoteEditButton } from '../MoteEditButton'
import type { MoteSaveToMotebookButtonProps } from '../MoteSaveToMotebookButton'
import { MoteSaveToMotebookButton } from '../MoteSaveToMotebookButton'
import { MoteLogoButton } from '../MotoLogoButton'
import type { PlaybackRateButtonProps } from '../PlaybackRateButton'
import { PlaybackRateButton } from '../PlaybackRateButton'
import type { ReactionButtonProps } from '../ReactionButton/ReactionButton'
import { ReactionButton } from '../ReactionButton/ReactionButton'
import type { TranscriptTranslateButtonProps } from '../TranscriptTranslateButton'
import { TranscriptTranslateButton } from '../TranscriptTranslateButton'
import { TranscriptionPreview } from '../Transcription'
import { MoteCardMediumActionsContainer } from './MoteCardMedium.styles'
import { MoteCardMediumLayout } from './MoteCardMediumLayout'
import type { MoteCardConfig } from './utils'
import { useMoteCardConfig } from './utils'

export interface MoteCardMediumProps
  extends MoteCardControlsProps,
    Omit<ComponentPropsWithoutRef<'div'>, 'onPlay' | 'onPause'> {
  homepageUrl: string
  isCreator: boolean
  playbackRate: PlaybackRateButtonProps['playbackRate']
  onPlaybackRateChange: PlaybackRateButtonProps['onClick']
  reaction?: ReactionButtonProps['value']
  onReact?: ReactionButtonProps['onChange']
  transcript?: string
  language?: string
  onTranslate?: TranscriptTranslateButtonProps['onChange']
  name: AvatarWithTooltipProps['name']
  avatarUrl?: AvatarWithTooltipProps['src']
  onEditClick: () => void
  audioData: AudioDataMinimal
  title: string
  onSaveToMotebook: MoteSaveToMotebookButtonProps['onSave']
  onDownload: MoteDownloadButtonProps['onDownload']
  inMotebook: MoteSaveToMotebookButtonProps['inMotebook']
  getCanUseMotebook: MoteSaveToMotebookButtonProps['getCanUseMotebook']
  hasHitMotebookLimit: MoteSaveToMotebookButtonProps['hasHitMotebookLimit']
}

interface WithoutTranscriptProps extends MoteCardMediumProps {
  config: MoteCardConfig
}

function WithoutTranscript(props: WithoutTranscriptProps): JSX.Element {
  return (
    <MoteCardMediumActionsContainer
      gridTemplateColumns={props.config.actionButtonPlacement}
    >
      {props.isCreator ? (
        <>
          <MoteDownloadButton
            onDownload={props.onDownload}
            textPosition='tooltip'
          />
          <MoteSaveToMotebookButton
            title={props.title}
            onSave={props.onSaveToMotebook}
            textPosition='tooltip'
            inMotebook={props.inMotebook}
            getCanUseMotebook={props.getCanUseMotebook}
            hasHitMotebookLimit={props.hasHitMotebookLimit}
          />
        </>
      ) : (
        <ReactionButton
          value={props.reaction}
          onChange={props.onReact!}
          textPosition='inside'
          pickerPlacement={props.config.reactionPickerPlacement}
        />
      )}
      <MoteLogoButton href={props.homepageUrl} />
    </MoteCardMediumActionsContainer>
  )
}

interface WithTranscriptProps extends MoteCardMediumProps {
  containerRef: React.RefObject<HTMLDivElement | null>
  config: MoteCardConfig
}

function WithTranscript(
  props: PropsWithChildren<WithTranscriptProps>
): JSX.Element {
  return (
    <>
      <TranscriptionPreview
        value={props.transcript!}
        rows={props.config.transcriptRows}
        language={props.language!}
        dynamicHeight
      />
      {props.children}
      <MoteCardMediumActionsContainer
        gridTemplateColumns={props.config.actionButtonPlacement}
      >
        {!props.isCreator && !props.children && (
          <ReactionButton
            value={props.reaction}
            onChange={props.onReact!}
            textPosition={props.config.firstButtonTextPosition}
            pickerPlacement={props.config.reactionPickerPlacement}
          />
        )}
        <TranscriptTranslateButton
          portalProps={{ containerRef: props.containerRef }}
          value={props.language!}
          onChange={props.onTranslate!}
          textPosition={props.config.translateButtonTextPosition}
          rootProps={{ m: 2, maxHeight: 'calc(100% - 1rem)' }}
          blockScrollOnMount={false}
          dialogSize='sm'
          useAbsolutePosition
        />
        {props.isCreator && (
          <MoteEditButton
            textPosition={props.config.secondButtonTextPosition}
            onClick={props.onEditClick}
          />
        )}
        <MoteLogoButton href={props.homepageUrl} />
      </MoteCardMediumActionsContainer>
    </>
  )
}

export const MoteCardMedium = forwardRef<
  HTMLDivElement,
  PropsWithChildren<MoteCardMediumProps>
>((props, ref): JSX.Element => {
  const { ref: containerRef, config } = useMoteCardConfig({
    isCreator: props.isCreator,
    isLoopy: Boolean(props.children),
    hasTranscript: Boolean(props.transcript?.trim())
  })
  const refs = useMergeRefs<HTMLDivElement | null>(ref, containerRef)
  const [stateProps, rootProps] = split(props, [
    'hasHitMotebookLimit',
    'getCanUseMotebook',
    'inMotebook',
    'onSaveToMotebook',
    'title',
    'onDownload',
    'children',
    'audioData',
    'duration',
    'currentTime',
    'state',
    'onPlay',
    'onPause',
    'onSeek',
    'onSeekEnd',
    'name',
    'avatarUrl',
    'homepageUrl',
    'isCreator',
    'playbackRate',
    'onPlaybackRateChange',
    'reaction',
    'onReact',
    'transcript',
    'language',
    'onTranslate',
    'onEditClick'
  ])

  const sharedProps = {
    ...stateProps,
    config
  }

  return (
    <MoteCardMediumLayout ref={refs} {...rootProps}>
      <MoteCardControls
        audioData={props.audioData}
        currentTime={stateProps.currentTime}
        duration={props.duration}
        state={stateProps.state}
        onPlay={stateProps.onPlay}
        onPause={stateProps.onPause}
        onSeek={stateProps.onSeek}
        onSeekEnd={stateProps.onSeekEnd}
      >
        {config.showAvatar &&
          (stateProps.state !== 'idle' ? (
            <PlaybackRateButton
              playbackRate={stateProps.playbackRate}
              onClick={stateProps.onPlaybackRateChange}
            />
          ) : (
            <AvatarWithTooltip
              size='sm'
              id='moteCardMediumAvatarTooltip'
              alt={stateProps.name}
              name={stateProps.name}
              src={stateProps.avatarUrl}
            />
          ))}
      </MoteCardControls>
      {props.transcript?.trim() ? (
        <WithTranscript {...sharedProps} containerRef={containerRef} />
      ) : (
        sharedProps.children || <WithoutTranscript {...sharedProps} />
      )}
    </MoteCardMediumLayout>
  )
})

if (__DEV__) {
  MoteCardMedium.displayName = 'MoteCardMedium'
}
